import { Anchor, Text } from '@mantine/core';
import { useNavigate } from 'react-router';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { DealView } from '../../../Deals/views/DealView';
import type { LeadExtended } from '../LeadViewController';

interface LeadFinancialProps {
  lead: LeadExtended;
}

const formatCurrency = (amount: number, currency: string = 'RUB') => {
  return `${amount.toLocaleString()} ${currency}`;
};

const formatDealDate = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Блок финансовой информации лида: сумма, вероятность, даты закрытия.
 */
export function LeadFinancial({ lead }: LeadFinancialProps) {
  const navigate = useNavigate();
  const dealDrawer = useDrawer(DealView, {
    openInTab: props => navigate(`/crm/deals/${props.dealId}`),
  });

  const dealId = lead.deal?.id ?? lead.deal_id ?? null;
  const dealNumber = lead.deal?.number ?? null;
  const dealCreatedAt = formatDealDate(lead.deal?.created_at);

  return (
    <ViewPanel>
      {lead.amount != null && lead.amount > 0 && (
        <InfoRow>
          <InfoRow.TitleCol>Сумма</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{formatCurrency(lead.amount, lead.currency)}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
      <InfoRow>
        <InfoRow.TitleCol>Вероятность</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <Text size='sm'>{lead.probability}%</Text>
        </InfoRow.ValueCol>
      </InfoRow>
      {dealId && (
        <InfoRow>
          <InfoRow.TitleCol>Сделка</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Anchor
              size='sm'
              href={`/crm/deals/${dealId}`}
              onClick={e => {
                e.preventDefault();
                dealDrawer.open({ dealId, isModal: true });
              }}
            >
              {dealNumber ? `Сделка №${dealNumber}` : `Сделка #${dealId}`}
              {dealCreatedAt ? ` от ${dealCreatedAt}` : ''}
            </Anchor>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
      {lead.expected_close_date && (
        <InfoRow>
          <InfoRow.TitleCol>Ожидаемое закрытие</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{new Date(lead.expected_close_date).toLocaleDateString()}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
      {lead.actual_close_date && (
        <InfoRow>
          <InfoRow.TitleCol>Фактическое закрытие</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{new Date(lead.actual_close_date).toLocaleDateString()}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}
    </ViewPanel>
  );
}
